import { useState, useEffect, useCallback, useMemo } from 'react';
import { Form, message } from 'antd';
import API from '../../../data/api';
import axios from 'axios';

/** State and actions for the tag assignment modal (assign tab + currently tagged items). */
export function useTagAssignmentController({ visible, tag, onSuccess, onMessage }) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [modelType, setModelType] = useState('Store');
    const [items, setItems] = useState([]);
    const [loadingItems, setLoadingItems] = useState(false);
    const [taggedItems, setTaggedItems] = useState([]);
    const [loadingTagged, setLoadingTagged] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);

    const api = API.Instance();
    const showMessage = useMemo(() => onMessage || ((type, msg) => message[type](msg)), [onMessage]);

    const fetchTaggedItems = useCallback(async () => {
        if (!tag) return;
        setLoadingTagged(true);
        try {
            const response = await api.requestTagItems(tag._id);
            setTaggedItems(response.items || []);
        } catch (error) {
            showMessage('error', 'Error al cargar elementos etiquetados: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoadingTagged(false);
        }
    }, [tag, api, showMessage]);

    const fetchItems = useCallback(
        async (type) => {
            setLoadingItems(true);
            try {
                let endpoint = '';
                switch (type) {
                    case 'User':
                        endpoint = '/user';
                        break;
                    case 'Client':
                        endpoint = '/client';
                        break;
                    case 'Store':
                        endpoint = '/store';
                        break;
                    case 'Product':
                        endpoint = '/products';
                        break;
                    case 'TradeCampaign':
                        endpoint = '/trade/campaign';
                        break;
                    case 'TradeContract':
                        endpoint = '/trade/contract';
                        break;
                    default:
                        break;
                }

                if (endpoint) {
                    const response = await axios.get(endpoint);
                    // Handle different response structures
                    const data = response.data.data || response.data.users || response.data.clients || response.data.stores || response.data;
                    setItems(Array.isArray(data) ? data : []);
                }
            } catch (error) {
                showMessage('error', 'Error al cargar elementos: ' + (error.response?.data?.message || error.message));
                setItems([]);
            } finally {
                setLoadingItems(false);
            }
        },
        [showMessage]
    );

    useEffect(() => {
        if (visible && tag) {
            const defaultModel = tag.applicableModels[0];
            setModelType(defaultModel);
            form.setFieldsValue({ modelType: defaultModel });
            fetchItems(defaultModel);
            fetchTaggedItems();
        }
    }, [visible, tag, form, fetchItems, fetchTaggedItems]);

    const handleModelTypeChange = (type) => {
        setModelType(type);
        setSelectedIds([]);
        fetchItems(type);
    };

    const handleAssign = async (values) => {
        if (selectedIds.length === 0) {
            showMessage('warning', 'Selecciona al menos un elemento');
            return;
        }

        setLoading(true);
        try {
            await api.requestAssignTag(tag._id, {
                modelType,
                modelIds: selectedIds,
                notes: values.notes
            });
            showMessage('success', `Etiqueta asignada a ${selectedIds.length} elemento(s)`);
            setSelectedIds([]);
            form.resetFields(['notes']);
            fetchTaggedItems();
            onSuccess();
        } catch (error) {
            showMessage('error', 'Error al asignar etiqueta: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = async (itemId, itemModelType) => {
        try {
            await api.requestRemoveTag(tag._id, {
                modelType: itemModelType,
                modelIds: [itemId]
            });
            showMessage('success', 'Etiqueta removida');
            fetchTaggedItems();
            onSuccess();
        } catch (error) {
            showMessage('error', 'Error al remover etiqueta: ' + (error.response?.data?.message || error.message));
        }
    };

    const getItemName = (item) => {
        return item.name || item.email || item.label || item._id;
    };

    return {
        form,
        loading,
        items,
        loadingItems,
        taggedItems,
        loadingTagged,
        selectedIds,
        setSelectedIds,
        handleModelTypeChange,
        handleAssign,
        handleRemove,
        getItemName
    };
}
